import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Upload, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Kronotex auto-fill specs
const KRONOTEX_SPECS = {
  'exquisit-8mm': { thickness: '8mm', plank_size: '1380 x 193 mm', traffic_class: 'AC4/Clasa 32', panels_per_pack: '8', sqm_per_pack: '2.131', bevel: '4V', brand: 'KRONOTEX' },
  'exquisit-plus-8mm': { thickness: '8mm', plank_size: '1380 x 244 mm', traffic_class: 'AC4/Clasa 32', panels_per_pack: '8', sqm_per_pack: '2.694', bevel: '4V', brand: 'KRONOTEX' },
  'amazone-10mm': { thickness: '10mm', plank_size: '1380 x 157 mm', traffic_class: 'AC5/Clasa 33', panels_per_pack: '6', sqm_per_pack: '1.300', bevel: '4V', brand: 'KRONOTEX' },
  'robusto-12mm': { thickness: '12mm', plank_size: '1375 x 188 mm', traffic_class: 'AC5/Clasa 33', panels_per_pack: '6', sqm_per_pack: '1.551', bevel: '4V', brand: 'KRONOTEX' },
  'mammut-12mm': { thickness: '12mm', plank_size: '1845 x 188 mm', traffic_class: 'AC5/Clasa 33', panels_per_pack: '4', sqm_per_pack: '1.387', bevel: '4V', brand: 'KRONOTEX' },
  'herringbone-8mm': { thickness: '8mm', plank_size: '592 x 155 mm', traffic_class: 'AC4/Clasa 32', panels_per_pack: '8', sqm_per_pack: '0.734', bevel: '2V', brand: 'KRONOTEX' },
  'mega-plus-8mm': { thickness: '8mm', plank_size: '1380 x 326 mm', traffic_class: 'AC4/Clasa 32', panels_per_pack: '4', sqm_per_pack: '1.800', bevel: '4V', brand: 'KRONOTEX' },
};

const SPEC_LABELS = {
  thickness: 'Grosime',
  plank_size: 'Dimensiune lamelă',
  traffic_class: 'Clasă trafic',
  panels_per_pack: 'Lamele/pachet',
  sqm_per_pack: 'mp/pachet',
  bevel: 'Teșitură',
  brand: 'Brand',
  water_resistant: 'Rezistență la apă',
};

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    parent_slug: '',
    description: '',
    image_url: '',
    order: 0,
    technical_specs: {}
  });

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) { console.error('Error fetching categories:', error); }
    finally { setLoading(false); }
  };

  const getToken = () => localStorage.getItem('token');
  const CHUNK_SIZE = 4 * 1024 * 1024;

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        slug: category.slug,
        parent_slug: category.parent_slug || '',
        description: category.description || '',
        image_url: category.image_url || '',
        order: category.order || 0,
        technical_specs: category.technical_specs || {},
        seo_title: category.seo_title || '',
        meta_description: category.meta_description || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({ name: '', slug: '', parent_slug: '', description: '', image_url: '', order: 0, technical_specs: {}, seo_title: '', meta_description: '' });
    }
    setDialogOpen(true);
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/ă/g, 'a').replace(/â/g, 'a').replace(/î/g, 'i').replace(/ș/g, 's').replace(/ț/g, 't')
      .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (name) => {
    setFormData({ ...formData, name, slug: editingCategory ? formData.slug : generateSlug(name) });
  };

  const handleAutoFillSpecs = () => {
    const specs = KRONOTEX_SPECS[formData.slug];
    if (specs) {
      setFormData(prev => ({ ...prev, technical_specs: { ...specs, ...prev.technical_specs } }));
      toast.success('Specificații Kronotex completate automat!');
    } else {
      toast.info('Nu există specificații predefinite pentru acest slug');
    }
  };

  const handleSpecChange = (key, value) => {
    setFormData(prev => ({ ...prev, technical_specs: { ...prev.technical_specs, [key]: value } }));
  };

  const uploadChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const initRes = await axios.post(`${API_URL}/api/upload/init`, { filename: file.name, total_chunks: totalChunks }, { headers: { Authorization: `Bearer ${getToken()}` } });
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
      const fd = new FormData(); fd.append('file', chunk, `chunk_${i}`);
      await axios.post(`${API_URL}/api/upload/chunk/${initRes.data.upload_id}/${i}`, fd, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }, timeout: 60000
      });
    }
    return (await axios.post(`${API_URL}/api/upload/complete/${initRes.data.upload_id}`, {
      filename: file.name, total_chunks: totalChunks, content_type: file.type
    }, { headers: { Authorization: `Bearer ${getToken()}` } })).data;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      let result;
      if (file.size > CHUNK_SIZE) { result = await uploadChunked(file); }
      else {
        const fd = new FormData(); fd.append('file', file);
        result = (await axios.post(`${API_URL}/api/upload`, fd, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        })).data;
      }
      setFormData(prev => ({ ...prev, image_url: result.url }));
      toast.success('Imagine încărcată!');
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.slug) { toast.error('Numele și slug-ul sunt obligatorii'); return; }
    try {
      const payload = { ...formData, parent_slug: formData.parent_slug || null };
      if (editingCategory) {
        await axios.put(`${API_URL}/api/categories/${editingCategory.slug}`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success('Categorie actualizată!');
      } else {
        await axios.post(`${API_URL}/api/categories`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
        toast.success('Categorie creată!');
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare');
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Sigur doriți să ștergeți această categorie?')) return;
    try {
      await axios.delete(`${API_URL}/api/categories/${slug}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Categorie ștearsă!');
      fetchCategories();
    } catch (error) { toast.error('Eroare la ștergere'); }
  };

  // Build tree
  const rootCats = categories.filter(c => !c.parent_slug).sort((a, b) => a.order - b.order);
  const getChildren = (parentSlug) => categories.filter(c => c.parent_slug === parentSlug).sort((a, b) => a.order - b.order);
  const imgSrc = (url) => url?.startsWith('/api') ? `${API_URL}${url}` : url;

  // Build flat list with depth
  const buildFlatList = () => {
    const result = [];
    const addCat = (cat, depth) => {
      result.push({ ...cat, depth });
      getChildren(cat.slug).forEach(child => addCat(child, depth + 1));
    };
    rootCats.forEach(cat => addCat(cat, 0));
    return result;
  };
  const flatList = buildFlatList();

  return (
    <AdminLayout title="Categorii & Colecții">
      <div data-testid="admin-categories">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[#A3A3A3] text-sm">Gestionați categoriile, subcategoriile și colecțiile (gamele). Structura: Categorie → Subcategorie → Colecție</p>
          </div>
          <button onClick={() => handleOpenDialog()} className="btn-primary flex items-center gap-2" data-testid="add-category-btn">
            <Plus size={18} /> Adaugă
          </button>
        </div>

        <div className="admin-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#121212] border-b border-white/10">
                <th className="p-4 text-left text-[#D4AF37] text-sm font-medium">Nume</th>
                <th className="p-4 text-left text-[#D4AF37] text-sm font-medium">Părinte</th>
                <th className="p-4 text-left text-[#D4AF37] text-sm font-medium">Ordine</th>
                <th className="p-4 text-left text-[#D4AF37] text-sm font-medium w-28">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {flatList.map(cat => {
                const hasSpecs = cat.technical_specs && Object.keys(cat.technical_specs).length > 0;
                return (
                  <tr key={cat.slug} className="border-b border-white/5 hover:bg-white/5" data-testid={`category-row-${cat.slug}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2" style={{ paddingLeft: `${cat.depth * 24}px` }}>
                        {cat.depth > 0 && <ChevronRight size={14} className="text-[#D4AF37]" />}
                        {cat.image_url && <img src={imgSrc(cat.image_url)} alt="" className="w-8 h-8 object-cover border border-white/10" />}
                        <div>
                          <span className="text-white font-medium">{cat.name}</span>
                          <span className="text-[#666] text-xs ml-2">/{cat.slug}</span>
                          {hasSpecs && <span className="text-[#D4AF37] text-xs ml-2">[Spec]</span>}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-[#A3A3A3] text-sm">{cat.parent_slug || '—'}</td>
                    <td className="p-4 text-[#A3A3A3] text-sm">{cat.order}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => handleOpenDialog(cat)} className="p-2 text-[#A3A3A3] hover:text-[#D4AF37] hover:bg-white/5" data-testid={`edit-${cat.slug}`}>
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => handleDelete(cat.slug)} className="p-2 text-[#A3A3A3] hover:text-red-400 hover:bg-white/5" data-testid={`delete-${cat.slug}`}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {categories.length === 0 && !loading && (
            <div className="text-center py-12 text-[#A3A3A3]">Nu există categorii</div>
          )}
        </div>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingCategory ? 'Editează Categorie' : 'Adaugă Categorie'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-1">Nume</label>
                  <input type="text" value={formData.name} onChange={e => handleNameChange(e.target.value)} className="form-input w-full" placeholder="Kronotex Mammut 12mm" />
                </div>
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-1">Slug (URL)</label>
                  <input type="text" value={formData.slug} onChange={e => setFormData({ ...formData, slug: e.target.value })} className="form-input w-full" placeholder="mammut-12mm" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-1">Categorie Părinte</label>
                  <Select value={formData.parent_slug || 'none'} onValueChange={v => setFormData({ ...formData, parent_slug: v === 'none' ? '' : v })}>
                    <SelectTrigger className="bg-[#1a1a1a] border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="none">— Categorie principală —</SelectItem>
                      {categories.map(c => (
                        <SelectItem key={c.slug} value={c.slug}>
                          {c.parent_slug ? `  └ ${c.name}` : c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-[#A3A3A3] text-sm mb-1">Ordine</label>
                  <input type="number" value={formData.order} onChange={e => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} className="form-input w-full" />
                </div>
              </div>

              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Descriere</label>
                <textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="form-input w-full h-20 resize-none" />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-[#A3A3A3] text-sm mb-1">Imagine Categorie/Colecție</label>
                <div className="flex gap-3 items-start">
                  {formData.image_url && (
                    <div className="relative w-24 h-16 bg-[#1a1a1a] border border-white/10 overflow-hidden flex-shrink-0">
                      <img src={imgSrc(formData.image_url)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setFormData(p => ({ ...p, image_url: '' }))} className="absolute top-0.5 right-0.5 bg-black/70 text-white p-0.5 rounded-full hover:bg-red-600"><X size={10} /></button>
                    </div>
                  )}
                  <div className="flex-1">
                    <input type="text" value={formData.image_url || ''} onChange={e => setFormData(p => ({ ...p, image_url: e.target.value }))} className="form-input w-full text-sm mb-1" placeholder="URL imagine..." />
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs bg-[#1a1a1a] border border-white/10 text-[#A3A3A3] hover:text-white px-2 py-1 flex items-center gap-1">
                      <Upload size={10} /> {uploading ? 'Se încarcă...' : 'Încarcă'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Technical Specs */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">Specificații Tehnice</label>
                  {KRONOTEX_SPECS[formData.slug] && (
                    <button onClick={handleAutoFillSpecs} className="text-xs bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-3 py-1 hover:bg-[#D4AF37]/20">
                      Auto-completare Kronotex
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(SPEC_LABELS).map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-[#666] text-xs mb-1">{label}</label>
                      <input type="text" value={formData.technical_specs?.[key] || ''} onChange={e => handleSpecChange(key, e.target.value)} className="form-input w-full text-sm" placeholder={`—`} />
                    </div>
                  ))}
                </div>
              </div>

              {/* SEO */}
              <div className="border-t border-white/10 pt-4">
                <label className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider mb-3 block">SEO</label>
                <div className="space-y-3">
                  <div>
                    <label className="text-[#666] text-xs mb-1 flex justify-between">
                      <span>SEO Title (max 60)</span>
                      <span className={`${(formData.seo_title?.length || 0) > 60 ? 'text-red-400' : 'text-[#666]'}`}>{formData.seo_title?.length || 0}/60</span>
                    </label>
                    <input type="text" value={formData.seo_title || ''} onChange={e => setFormData({...formData, seo_title: e.target.value})} className="form-input w-full text-sm" placeholder="Ex: Parchet Laminat Kronotex Mammut 12mm" />
                  </div>
                  <div>
                    <label className="text-[#666] text-xs mb-1 flex justify-between">
                      <span>Meta Description (max 155)</span>
                      <span className={`${(formData.meta_description?.length || 0) > 155 ? 'text-red-400' : 'text-[#666]'}`}>{formData.meta_description?.length || 0}/155</span>
                    </label>
                    <textarea value={formData.meta_description || ''} onChange={e => setFormData({...formData, meta_description: e.target.value})} className="form-input w-full text-sm h-16 resize-none" placeholder="Ex: Descoperă gama Kronotex Mammut 12mm..." />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button onClick={() => setDialogOpen(false)} className="btn-secondary">Anulează</button>
                <button onClick={handleSubmit} className="btn-primary" data-testid="save-category-btn">
                  {editingCategory ? 'Salvează' : 'Creează'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCategories;
